/**
 * Lift2Fit — Terms of Service (v1).
 *
 * Deliberately concrete about what Lift2Fit is not: not a healthcare provider,
 * not the coach's employer or supervisor, and not a guarantee that automated
 * output is right. The governing-law clause names no invented jurisdiction — it
 * points at where the operating entity is established and defers the specific
 * country to the operator, which a lawyer must fill in before launch.
 */
import { LegalCallout, LegalList, LegalP, LegalSection } from './LegalLayout';
import { CONTACT_PRIVACY, CONTACT_SUPPORT, DOMAIN, POLICY_VERSION, effectiveDateLabel } from './policy';

export function TermsOfService() {
  return (
    <>
      <LegalCallout title="Read this with the other two documents">
        <LegalP>
          These Terms are version {POLICY_VERSION} and take effect on {effectiveDateLabel()}. They incorporate
          our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/privacy">
            Privacy Policy
          </a>{' '}
          and our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/health-disclaimer">
            Health &amp; Medical Disclaimer
          </a>
          . By creating an account you confirm that you have read and understand all three.
        </LegalP>
      </LegalCallout>

      <LegalSection id="agreement" title="1. The agreement">
        <LegalP>
          These Terms of Service are a legal agreement between you and Lift2Fit ({DOMAIN}) covering your use of
          the Lift2Fit web app, mobile and watch apps, coach workspace and website. If you do not agree to them,
          do not create an account.
        </LegalP>
        <LegalP>
          If you use Lift2Fit for a business, you confirm you have authority to bind that business to these Terms.
        </LegalP>
      </LegalSection>

      <LegalSection id="eligibility" title="2. Who can use Lift2Fit">
        <LegalP>
          You must be at least <strong>16 years old</strong> to create an account. Lift2Fit is not for children
          under 16, and we do not offer a parental-consent flow. By creating an account you confirm you meet this
          requirement.
        </LegalP>
        <LegalP>
          You must give accurate information when you sign up and keep your login credentials secure. You are
          responsible for activity under your account.
        </LegalP>
      </LegalSection>

      <LegalSection id="medical" title="3. Not medical advice; you train at your own risk">
        <LegalCallout tone="warning" title="The most important clause in these Terms">
          <LegalP>
            Lift2Fit is a training log and coaching tool. It is <strong>not</strong> a doctor, physiotherapist,
            dietician, psychologist or any other healthcare provider. Nothing in Lift2Fit is medical advice,
            diagnosis, treatment or prescription, and it does not replace advice from a qualified professional.
          </LegalP>
        </LegalCallout>
        <LegalP>
          You are responsible for your own safety. Before you start or change a training programme, consult a
          qualified professional, especially if you are pregnant, injured, unwell, or have a medical condition.
          You assume the risk of the training you choose to do. Stop immediately and seek professional help if you
          feel pain, dizziness, faintness, chest pain, or any other concerning symptom.
        </LegalP>
        <LegalP>
          The full position is in our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/health-disclaimer">
            Health &amp; Medical Disclaimer
          </a>
          , which forms part of these Terms.
        </LegalP>
      </LegalSection>

      <LegalSection id="ai" title="4. Automated coaching is automated">
        <LegalP>
          Lift2Fit generates coaching suggestions, programme changes and review summaries automatically. They are
          produced by software rules and, in future versions, by our own models. They may be wrong, out of date or
          unsuitable for you. They are not personalised medical advice and no human reviews every suggestion.
        </LegalP>
        <LegalP>
          You decide whether to follow a suggestion. You can ignore, edit or delete anything the automated coach
          produces, and you should use your own judgement and, where appropriate, professional advice.
        </LegalP>
      </LegalSection>

      <LegalSection id="coaches" title="5. Coaches are independent">
        <LegalP>
          Coaches, trainers and gyms on Lift2Fit are <strong>independent</strong> from us. We do not employ them,
          we do not supervise or endorse the programmes they write, and we are not a party to the coaching
          relationship between a coach and a client. A programme a coach writes is theirs, not our advice, and any
          agreement about fees, sessions or results is between the coach and the client.
        </LegalP>
        <LegalP>
          If you are a coach, you are a separate controller of your clients’ data (see section 8 of the Privacy
          Policy), you must have a lawful basis to process it, and you must not use Lift2Fit to do anything
          unlawful or to misrepresent your qualifications.
        </LegalP>
        <LegalP>
          If you are a client of a coach, your remedies in relation to the coaching itself are against that coach.
          If you believe a coach has broken these Terms, tell us at {CONTACT_SUPPORT} and we will investigate the
          use of the platform.
        </LegalP>
      </LegalSection>

      <LegalSection id="acceptable-use" title="6. Acceptable use">
        <LegalP>You agree not to:</LegalP>
        <LegalList
          items={[
            'Use Lift2Fit for anything unlawful, or to harm yourself or anyone else.',
            'Attempt to access another account’s data, probe or breach our security, or work around the sharing controls.',
            'Upload malicious code, scrape the service, or resell it without our written permission.',
            'Misrepresent your identity or qualifications, including holding yourself out as a qualified professional when you are not.',
            'Use another person’s health data without a lawful basis.',
          ]}
        />
        <LegalP>
          We may suspend or close an account that breaks these rules, and we will tell you why unless the law
          prevents us.
        </LegalP>
      </LegalSection>

      <LegalSection id="plans" title="7. Plans and payment">
        <LegalP>
          Lift2Fit offers a free tier and paid tiers. Paid plans are arranged directly with us or with a coach or
          gym; Lift2Fit does not currently process card payments through the app. Where a plan is paid, the price,
          billing period and cancellation terms are the ones you agreed when you bought it. Prices and plan
          limits may change for future periods with reasonable notice.
        </LegalP>
      </LegalSection>

      <LegalSection id="content" title="8. Your content and our intellectual property">
        <LegalP>
          The training data, programmes, notes and messages you create are yours. You give us a licence to host,
          process and use them to operate the service, to provide it to you and, where you have consented, to
          improve the service and train and evaluate our models, as described in the Privacy Policy. That licence
          ends when you delete the content or close your account, except for copies we must keep by law.
        </LegalP>
        <LegalP>
          The Lift2Fit name, logo, software and the design of the service belong to us or our licensors. These
          Terms do not give you any right to them beyond using the service as intended.
        </LegalP>
      </LegalSection>

      <LegalSection id="availability" title="9. Availability and changes to the service">
        <LegalP>
          We work to keep Lift2Fit available, but we do not promise uninterrupted or error-free service. We may
          change, add or remove features, and we may suspend the service for maintenance. If we discontinue the
          service, we will give you reasonable notice and a way to export your data.
        </LegalP>
      </LegalSection>

      <LegalSection id="liability" title="10. Disclaimers and limitation of liability">
        <LegalP>
          To the fullest extent the law allows, Lift2Fit is provided “as is”, and we exclude implied warranties
          that the service is fit for a particular purpose or that automated output is accurate. We do not exclude
          or limit liability that cannot lawfully be excluded — including for death or personal injury caused by
          our negligence, or for fraud.
        </LegalP>
        <LegalP>
          Subject to that, we are not liable for indirect or consequential loss, for lost data where you have not
          kept your own copy, or for decisions you take based on automated coaching output. Nothing in these Terms
          affects your statutory consumer rights.
        </LegalP>
      </LegalSection>

      <LegalSection id="termination" title="11. Ending the agreement">
        <LegalP>
          You can stop using Lift2Fit and close your account at any time. We may suspend or close your account if
          you materially break these Terms, if we are required to by law, or if we discontinue the service. Where
          we can, we will give you notice and the chance to export your data first.
        </LegalP>
      </LegalSection>

      <LegalSection id="law" title="12. Governing law">
        <LegalP>
          These Terms are governed by the law of the country in which the Lift2Fit entity operating the service is
          established, and its courts have exclusive jurisdiction, except that mandatory consumer protections in
          your country of residence continue to apply. The specific country will be named in this clause by the
          operator before public launch.
        </LegalP>
      </LegalSection>

      <LegalSection id="changes" title="13. Changes to these Terms">
        <LegalP>
          We may update these Terms. For a material change we will increase the version number, change the
          effective date, and tell you before it takes effect. Continuing to use Lift2Fit after a material change
          requires your acceptance of the new version; we will not treat silence as agreement.
        </LegalP>
      </LegalSection>

      <LegalSection id="contact" title="14. Contact">
        <LegalP>
          Questions about these Terms: {CONTACT_SUPPORT}. Privacy and data-protection questions:{' '}
          {CONTACT_PRIVACY}.
        </LegalP>
        <LegalP>
          Lift2Fit · {DOMAIN}. One stop for all your fitness needs.
        </LegalP>
      </LegalSection>
    </>
  );
}
